import { keepPreviousData, QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { UpdateInput } from './input/update-input'
import {
    deleteCampaign,
    deleteCampaignColumn,
    fetchCampaign,
    fetchCampaigns,
    reorderCampaignColumns,
    sortColumnCandidates,
    storeCampaign,
    storeCampaignColumn,
    updateCampaign,
    updateCampaignColumn,
} from './api'
import { Campaign } from './campaign'
import { setSearchQueriesData } from '../user/hooks'
import { onErrorNotifyAboutValidationError } from '../util'

export function useUpdateCampaign(campaignId?: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (data: UpdateInput) => updateCampaign(campaignId!, data),
        onSuccess: (campaign) => {
            setCampaignsQueriesData(queryClient, campaign)
            setSearchQueriesData(queryClient, 'campaigns', campaign)
        },
    })
}

export function useDeleteCampaign() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: deleteCampaign,
        onSuccess() {
            queryClient.invalidateQueries({
                queryKey: ['campaigns'],
            })
        },
    })
}

export function useCampaigns(...args: Parameters<typeof fetchCampaigns>) {
    return useQuery({
        queryFn: () => fetchCampaigns(...args),
        queryKey: ['campaigns', args],
        placeholderData: keepPreviousData,
    })
}

export function useCampaign(campaignId: string) {
    return useQuery({
        queryFn: () => fetchCampaign(campaignId),
        queryKey: ['campaigns', campaignId],
    })
}

export function useStoreCampaign() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: storeCampaign,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['campaigns'],
            })
        },
    })
}

function setCampaignsQueriesData(queryClient: QueryClient, patch: Campaign) {
    queryClient.setQueriesData(
        {
            queryKey: ['campaigns'],
        },
        (data: undefined | Campaign | Awaited<ReturnType<typeof fetchCampaigns>>) => {
            // The facebook-accounts and leadgen-forms queries share the ['campaigns'] prefix.
            if (!data || Array.isArray(data)) {
                return data
            }

            if ('id' in data) {
                if (data.id === patch.id) {
                    return patch
                }

                return data
            }

            if (!Array.isArray(data.data)) {
                return data
            }

            data.data = data.data.map((user) => {
                if (user.id === patch.id) {
                    return patch
                }

                return user
            })

            return data
        }
    )
}

export function useStoreCampaignColumn(campaignId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: storeCampaignColumn(campaignId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['campaigns', campaignId],
            })
        },
    })
}

export function useDeleteCampaignColumn(campaignId: string, columnId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: () => deleteCampaignColumn(campaignId, columnId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['campaigns', campaignId],
            })
        },
        onError: onErrorNotifyAboutValidationError,
    })
}

export function useUpdateCampaignColumn(campaignId: string, columnId?: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: updateCampaignColumn(campaignId, columnId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['campaigns', campaignId],
            })
        },
        onError: onErrorNotifyAboutValidationError,
    })
}

export function useReorderCampaignColumn(campaignId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: reorderCampaignColumns(campaignId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['campaigns', campaignId],
            })
        },
    })
}

export function useSortColumnCandidates(campaignId: string, columnId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: sortColumnCandidates(campaignId, columnId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['campaigns', campaignId],
            })

            queryClient.invalidateQueries({
                queryKey: ['candidates', 'column', campaignId, columnId],
                exact: false,
            })
        },
        onError: onErrorNotifyAboutValidationError,
    })
}
